import { Invoiceable, InvoiceLine, Party, PAYMENT_MEANS_OPTIONS } from "@/modules/facturae/domain/Invoiceable";
import { Product, ProductConfiguration } from "../domain/Product";
import { Sequence } from "../domain/Sequence";
import { RecurringInvoiceContract } from "../domain/RecurringInvoiceContract";
import { RecurringInvoiceEditorScreenData } from "../domain/RecurringInvoiceEditorScreenData";
import { Configuration } from "../domain/Configuration";

export class VueRecurringInvoiceEditorScreenData implements RecurringInvoiceEditorScreenData {
    recurringInvoice: RecurringInvoiceContract;
    customers: Party[];
    availableSequences: Sequence[];
    selectedSequence: Sequence;
    products: Product[];
    excelPopup: boolean;
    deleteAllLinesPopup: boolean;
    file: File;
    loading: boolean;
    snackbar: Object;
    showErrors: boolean;
    showErrorsDialog: boolean;
    showErrorsDialogSupplier: boolean;
    asyncJobState: string;
    asyncJobId: number;
    resolvePopup: Function;
    invoiceConfiguration: Configuration;
    intervalOptions: Object
    periodOptions: Object
    expirationOptions: Object
    nextInvoiceInfo: Object
    startDialog: boolean
    stopDialog: boolean
    realtedInvoicesPopup: boolean
    relatedInvoices: Invoiceable[]
    loadingRelatedInvoices: boolean
    realtedInvoicesHeaders: Object
    payment_options: Object[];
    showErrorExcelDialog: boolean;
    error_excel_message: string;
    productConfiguration: ProductConfiguration;
    hasActiveContract: boolean;

    constructor() {
        this.recurringInvoice = null
        this.customers = []
        this.availableSequences = []
        this.selectedSequence = new Sequence()
        this.products = []
        this.excelPopup = false
        this.deleteAllLinesPopup = false
        this.file = null
        this.loading = false
        this.snackbar = {
            ['isVisible']: false,
            text: '',
            color: '',
            timeout: 3000,
        }
        this.showErrors = false
        this.showErrorsDialog = false
        this.showErrorsDialogSupplier = false
        this.asyncJobState = ''
        this.asyncJobId = 0
        this.invoiceConfiguration = new Configuration()
        this.intervalOptions = [
            {
                text: 'Mensual',
                value: 'mensual'
            },
            {
                text: 'Trimestral',
                value: 'trimestral'
            },
            {
                text: 'Semestral',
                value: 'semestral'
            },
            {
                text: 'Anual',
                value: 'anual'
            }
        ]
        this.periodOptions = [
            {
                text: 'Adelantado',
                value: 'adelantado'
            },
            {
                text: 'Atrasado',
                value: 'atrasado'
            }
        ]
        this.expirationOptions = [
            {
                text: 'A la vista',
                value: 0
            },
            {
                text: '30 días',
                value: 30
            },
            {
                text: '60 días',
                value: 60
            },
            {
                text: '90 días',
                value: 90
            }
        ]
        this.nextInvoiceInfo = {
            'start_date': '',
            'end_date': '',
        }
        this.startDialog = false
        this.stopDialog = false
        this.realtedInvoicesPopup = false
        this.loadingRelatedInvoices = false
        this.realtedInvoicesHeaders = [
            {
                'text': 'ID',
                sorteable: false,
                'value': 'id'
            },
            {
                text: "Número de factura",
                sortable: false,
                value: "invoice_number",
            },
            {
                text: "Fecha de factura",
                sortable: false,
                value: "invoice_date",
            },
        ]
        this.relatedInvoices = []
        this.payment_options =  PAYMENT_MEANS_OPTIONS
        this.showErrorExcelDialog = false
        this.error_excel_message = 'Ha ocurrido un error inesperado al procesar el archivo. Por favor, inténtelo de nuevo. Si el problema persiste, contacte con soporte.'
        this.hasActiveContract = false;
    }

    successMessage(message: string): void {
        this.snackbar['text'] = message
        this.snackbar['color'] = 'success'
        this.snackbar['isVisible'] = true
    }

    errorMessage(message: string): void {
        this.snackbar['text'] = message
        this.snackbar['color'] = 'error'
        this.snackbar['isVisible'] = true
    }

    async askForFile(): Promise<unknown> {
        this.excelPopup = true
        return new Promise((resolve, reject) => {
            this.resolvePopup = resolve
        })
    }

    async deleteAllLinesDialog(): Promise<unknown> {
        this.deleteAllLinesPopup = true
        return new Promise((resolve, reject) => {
            this.resolvePopup = resolve
        })
    }

    setLoading(loading: boolean): void {
        this.loading = loading
    }

    setLines(lines: InvoiceLine[]): void {
        console.log('setting lines')
        this.recurringInvoice.invoice.lines = [...lines]
    }

    resetLines(): void {
        console.log('resetting lines')
        this.recurringInvoice.invoice.lines = [...this.recurringInvoice.invoice.lines]
    }

    setRecurringInvoice(recurringInvoice: RecurringInvoiceContract): void {
        recurringInvoice.compute()
        this.recurringInvoice = recurringInvoice
        this.setLines(recurringInvoice.invoice.lines)
    }

    setCustomers(customers: Party[]): void {
        this.customers = customers
    }

    setCustomer(customer: Party): void {
        this.recurringInvoice.invoice.customer = customer
    }

    setErrorDialog(showErrorsDialog: boolean): void {
        this.showErrorsDialog = showErrorsDialog
    }

    setErrorDialogSupplier(showErrorsDialogSupplier: boolean): void {
        this.showErrorsDialogSupplier = showErrorsDialogSupplier
    }

    setProducts(products: Product[]): void {
        this.products = products;
    }
    setSequence(sequence: Sequence): void {
        this.selectedSequence = sequence
    }

    setSequences(sequences: Sequence[]): void {
        this.availableSequences = sequences
    }

    confirmPopup(): void {
        this.excelPopup = false
        this.deleteAllLinesPopup = false
        this.resolvePopup(true)
    }

    cancelPopup(): void {
        this.excelPopup = false
        this.deleteAllLinesPopup = false
        this.resolvePopup(false)
    }

    setCongiguration(configuration: Configuration): void {
        this.invoiceConfiguration = configuration
        let filteredSequences = configuration.sequences.filter(seq => seq.is_for_recurring_contracts)
        this.setSequences(filteredSequences)
    }

    setPaymentMeans(payment_means: string): void{
        if (!this.recurringInvoice.invoice.flag_payment_means_modified){
            this.recurringInvoice.invoice.payment_means = payment_means
        }
    }

    setPaymentInformation(payment_information: string): void{
        if (!this.recurringInvoice.invoice.flag_payment_information_modified){
            this.recurringInvoice.invoice.payment_information = payment_information
        }
    }

    setErrorExcelDialog(showErrorExcelDialog: boolean): void {
        this.showErrorExcelDialog = showErrorExcelDialog
    }

    setProductConfiguration(productConfiguration: ProductConfiguration): void {
        this.productConfiguration = productConfiguration
    }

}